import React, { useState, useRef, useEffect } from 'react';
import SyllabusScheduleTable from './SyllabusScheduleTable';
import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';
import ActionLink from 'components/elements/molecules/ActionLink/ActionLink';
import ConfirmModal from 'components/modals/Confirm/ConfirmModal';

export default function SyllabusUploadStep2({
  rows,
  onChange,
  onBack,
  onConfirm,
  loading,
  courseId,
  backLabel = 'Back',
  syllabusData,
  isManageLectureScheduleMode = false,
  isFileUploadFlow = false,
  onHasUnsavedChanges,
  onUpload,
}) {
  const [error, setError] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    onHasUnsavedChanges?.(hasUnsavedChanges);
  }, [hasUnsavedChanges]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  // Tracks rows with pending cell edits applied — used for validation
  const [effectiveRows, setEffectiveRows] = useState(rows);
  const tableRef = useRef(null);

  const handleChange = (updatedRows) => {
    onChange(updatedRows);
  };

  const isRowMissingDateOrTopic = (row) => {
    const topic = row.topic || row.name || '';
    const isDateMissing = !row.date && !row.invalidLectureTopic;
    return isDateMissing || !topic || topic.trim() === '';
  };

  const hasNoRows = effectiveRows.length === 0;
  const hasIncompleteRows = effectiveRows.some(
    (row) => row.checkValidity !== false && isRowMissingDateOrTopic(row),
  );
  const hasIncompleteRowsStrict = effectiveRows.some((row) =>
    isRowMissingDateOrTopic(row),
  );
  const visibleEffectiveRows =
    syllabusData?.enabled === true
      ? effectiveRows.filter((row) => !row.skipWeek)
      : effectiveRows;
  const missingDatesCount = visibleEffectiveRows.filter(
    (row) =>
      row.checkValidity !== false && !row.date && !row.invalidLectureTopic,
  ).length;
  const missingTopicsCount = visibleEffectiveRows.filter((row) => {
    if (row.checkValidity === false) return false;
    const t = row.topic || row.name || '';
    return !t || t.trim() === '';
  }).length;
  const ambiguousTopicsCount = visibleEffectiveRows.filter(
    (row) => row.invalidLectureTopic,
  ).length;
  const issueParts = [
    missingDatesCount > 0 &&
      `${missingDatesCount} missing date${missingDatesCount > 1 ? 's' : ''}`,
    missingTopicsCount > 0 &&
      `${missingTopicsCount} missing topic${missingTopicsCount > 1 ? 's' : ''}`,
    ambiguousTopicsCount > 0 &&
      `${ambiguousTopicsCount} ambiguous topic${ambiguousTopicsCount > 1 ? 's' : ''}`,
    hasUnsavedChanges && 'Unsaved changes',
  ].filter(Boolean);
  const issueSummary = issueParts.join(', ');
  const validationError = hasNoRows
    ? 'Please add at least one lecture week before confirming.'
    : hasIncompleteRows
      ? 'Please complete all rows with date and lecture topic before confirming.'
      : '';
  const isConfirmDisabled = saving || hasNoRows || hasIncompleteRowsStrict;

  const handleConfirm = async () => {
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setSaving(true);
    try {
      const savedCount = (await tableRef.current?.saveAll()) ?? 0;
      onConfirm({ savedCount });
    } catch (err) {
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowCancelConfirm(true);
    } else {
      onBack();
    }
  };

  const handleCancelConfirm = (confirmed) => {
    setShowCancelConfirm(false);
    if (confirmed) onBack();
  };

  return (
    <div className="syllabus-step">
      {showCancelConfirm && (
        <ConfirmModal
          model={{
            message:
              'You have unsaved changes. Are you sure you want to cancel?',
          }}
          onClose={handleCancelConfirm}
        />
      )}

      <div className="main-con">
        <div className="modal-header">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <div className="syllabus-step-title">
                {isManageLectureScheduleMode
                  ? syllabusData?.originalFilename
                    ? 'Review your lecture schedule'
                    : 'Update your lecture schedule'
                  : isFileUploadFlow
                    ? 'Review your Lecture Schedule'
                    : 'Create your Lecture Schedule'}
              </div>
              <p className="syllabus-step-desc">
                {rows.length === 0
                  ? 'Add lecture weeks with dates and topics.'
                  : 'Review and edit any dates or topics before confirming.'}
              </p>
            </div>
            <div className="text-right">
              <p className="syllabus-step-filename">
                {'Syllabus: '}
                {syllabusData?.originalFilename ? (
                  <>
                    <a
                      href={syllabusData.s3Url}
                      download={syllabusData.originalFilename}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {(() => {
                        const name = syllabusData.originalFilename;
                        const dotIndex = name.lastIndexOf('.');
                        const base =
                          dotIndex !== -1 ? name.slice(0, dotIndex) : name;
                        const ext = dotIndex !== -1 ? name.slice(dotIndex) : '';
                        return base.length > 15
                          ? `${base.slice(0, 15)}...${ext}`
                          : name;
                      })()}
                    </a>
                  </>
                ) : (
                  <ActionLink
                    toolTipText="You can upload your syllabus file, and we’ll try to set up your lecture topics and dates."
                    toolTipPosition="left"
                    linkClassName="d-flex align-items-center"
                    onClick={onUpload}
                  >
                    Upload
                  </ActionLink>
                )}
              </p>
              {issueSummary && (
                <p className="syllabus-step-issue-summary">{issueSummary}</p>
              )}
            </div>
          </div>
        </div>
        <div className="section-con">
          <SyllabusScheduleTable
            ref={tableRef}
            rows={rows}
            onChange={handleChange}
            courseId={courseId}
            syllabusData={syllabusData}
            onHasUnsavedChanges={setHasUnsavedChanges}
            onEffectiveRowsChange={setEffectiveRows}
          />
        </div>

        <div className="syllabus-step-footer">
          <div />
          <div className="d-flex flex-column align-items-end">
            <div className="d-flex align-items-center" style={{ gap: '1rem' }}>
              <RectangularButton onClick={handleBack}>
                {backLabel}
              </RectangularButton>
              <RectangularButton
                darkButton
                onClick={handleConfirm}
                disabled={isConfirmDisabled}
              >
                {saving || loading ? 'Saving...' : 'Save schedule'}
              </RectangularButton>
            </div>
            {(validationError || error) && (
              <div className="syllabus-error-inline mt-2">
                {validationError || error}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
